import { createContext, useContext } from 'react';
import ReactMarkdown, { uriTransformer } from 'react-markdown';

import { faWandMagicSparkles } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';
import remarkGfm from 'remark-gfm';

import { LoadingDots } from '../../../components/LoadingDots';
import { getLoadingStatusLabel } from '../getLoadingStatusLabel';
import { AssistantMessageActions } from './AssistantMessageActions';

const REMARK_PLUGINS = [remarkGfm];

const SUGGESTION_SCHEME = 'suggestion:';

// Contexte permettant au renderer `a` de savoir s'il doit rendre les options `suggestion:`
// comme des chips cliquables (uniquement sur le dernier message) et quel handler appeler.
const SuggestionContext = createContext({ interactive: false, onSelect: () => {} });

// Préserve notre schéma `suggestion:` (sinon `uriTransformer` le retirerait car non listé
// parmi les schémas sûrs), tout en gardant l'assainissement par défaut pour les vrais liens.
const transformLinkUri = (uri) => (uri?.startsWith(SUGGESTION_SCHEME) ? uri : uriTransformer(uri));

// Le modèle écrit les options sous la forme `[libellé](suggestion:message à envoyer)`. Or une
// destination de lien markdown ne peut pas contenir d'espaces : sans traitement, remark ne la
// parse pas comme un lien et l'affiche en texte brut. On URL-encode donc la destination en amont
// du rendu pour en faire un lien valide (le payload est redécodé au clic). Le libellé (entre
// crochets) n'est jamais touché. Tolère aussi la forme vide `suggestion:` (payload = libellé).
const normalizeSuggestionLinks = (content) =>
    content.replace(
        /\[([^\]]+)\]\(suggestion:([^)]*)\)/g,
        (_match, label, payload) => `[${label}](${SUGGESTION_SCHEME}${encodeURIComponent(payload.trim())})`
    );

// Texte brut d'un noeud de lien markdown (libellé affiché, utilisé comme message de repli).
const getNodeText = (node) =>
    (node?.children ?? []).map((child) => (child.type === 'text' ? child.value : getNodeText(child))).join('');

// Message à envoyer : payload décodé de la destination si présent, sinon le libellé affiché.
const getSuggestionMessage = (href, node) => {
    const raw = href.slice(SUGGESTION_SCHEME.length);
    let decoded = '';
    try {
        decoded = decodeURIComponent(raw);
    } catch {
        decoded = raw;
    }
    return decoded.trim() || getNodeText(node);
};

const SuggestionChip = ({ children, href, node }) => {
    const { interactive, onSelect } = useContext(SuggestionContext);
    // Sur un ancien message (non interactif), on garde l'option lisible mais en texte simple.
    if (!interactive) {
        return <span className='font-medium text-slate-700'>{children}</span>;
    }
    return (
        <button
            type='button'
            onClick={() => onSelect(getSuggestionMessage(href, node))}
            className='mx-0.5 inline-flex items-center rounded-full border border-lime-300 bg-lime-50 px-2.5 py-0.5 text-[12px] font-medium text-lime-800 transition-colors hover:bg-lime-100 active:bg-lime-200 cursor-pointer'
        >
            {children}
        </button>
    );
};

const MARKDOWN_COMPONENTS = {
    p: (props) => (
        <p
            className='my-2 first:mt-0 last:mb-0'
            {...props}
        />
    ),
    ul: (props) => (
        <ul
            className='my-2 list-disc space-y-0.5 pl-5 first:mt-0 last:mb-0'
            {...props}
        />
    ),
    ol: (props) => (
        <ol
            className='my-2 list-decimal space-y-0.5 pl-5 first:mt-0 last:mb-0'
            {...props}
        />
    ),
    h1: (props) => (
        <h1
            className='mb-1.5 mt-3 text-base font-semibold first:mt-0'
            {...props}
        />
    ),
    h2: (props) => (
        <h2
            className='mb-1.5 mt-3 text-sm font-semibold first:mt-0'
            {...props}
        />
    ),
    h3: (props) => (
        <h3
            className='mb-1.5 mt-3 text-sm font-semibold first:mt-0'
            {...props}
        />
    ),
    hr: (props) => (
        <hr
            className='my-3 border-slate-200'
            {...props}
        />
    ),
    table: (props) => (
        <div className='mt-2 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm'>
            <table
                className='min-w-full border-separate border-spacing-0 text-[12px]/5'
                {...props}
            />
        </div>
    ),
    th: (props) => (
        <th
            className='sticky top-0 z-10 border-b border-slate-200 bg-slate-100 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700 first:rounded-tl-xl last:rounded-tr-xl'
            {...props}
        />
    ),
    tr: (props) => (
        <tr
            className='odd:bg-white even:bg-slate-50/70 hover:bg-slate-100/70'
            {...props}
        />
    ),
    td: (props) => (
        <td
            className='border-b border-slate-100 px-3 py-2 align-top text-slate-700'
            {...props}
        />
    ),
    a: ({ href, children, node, ...props }) =>
        href?.startsWith(SUGGESTION_SCHEME) ? (
            <SuggestionChip
                href={href}
                node={node}
            >
                {children}
            </SuggestionChip>
        ) : (
            <a
                href={href}
                className='font-medium text-blue-700 underline underline-offset-2 transition-colors hover:text-blue-800'
                target='_blank'
                rel='noopener noreferrer'
                {...props}
            >
                {children}
            </a>
        ),
    code: ({ inline, children, ...props }) =>
        inline ? (
            <code
                className='rounded-[3px] bg-gray-200 px-1 py-px text-xs'
                {...props}
            >
                {children}
            </code>
        ) : (
            <pre className='overflow-x-auto rounded-md bg-gray-200 p-2 text-xs'>
                <code {...props}>{children}</code>
            </pre>
        ),
};

export const AiChatThread = ({
    messages,
    isLoading,
    loadingStatus,
    ratings,
    onRate,
    messagesEndRef,
    onConfirmSql,
    onReplay,
    onSuggestionClick,
    t,
    userInitials,
}) => {
    return (
        <div className='flex min-h-0 flex-col gap-3 px-4 pb-2 pt-4'>
            {messages.map((msg, i) => (
                <div
                    key={i}
                    className={clsx(
                        'flex max-w-[88%] items-start gap-2',
                        msg.role === 'user' ? 'self-end' : 'self-start'
                    )}
                >
                    {msg.role === 'assistant' ? (
                        <div className='mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-slate-900 text-lime-200'>
                            <FontAwesomeIcon
                                icon={faWandMagicSparkles}
                                className='text-xs'
                            />
                        </div>
                    ) : null}
                    <div className='flex min-w-0 flex-col gap-1.5'>
                        <div
                            className={clsx(
                                'px-3 py-2.5 text-[13px] leading-normal min-w-0',
                                msg.role === 'user'
                                    ? 'rounded-2xl rounded-tr-sm bg-slate-900 text-slate-50'
                                    : 'rounded-2xl rounded-tl-sm border border-gray-200 bg-white text-slate-900 shadow-[0_4px_14px_rgba(15,23,42,0.05)]'
                            )}
                        >
                            {msg.role === 'user' ? (
                                msg.content
                            ) : (
                                <SuggestionContext.Provider
                                    value={{
                                        interactive: i === messages.length - 1 && !isLoading,
                                        onSelect: onSuggestionClick,
                                    }}
                                >
                                    <ReactMarkdown
                                        remarkPlugins={REMARK_PLUGINS}
                                        components={MARKDOWN_COMPONENTS}
                                        transformLinkUri={transformLinkUri}
                                    >
                                        {normalizeSuggestionLinks(msg.content)}
                                    </ReactMarkdown>
                                </SuggestionContext.Provider>
                            )}
                        </div>
                        {msg.role === 'assistant' && (
                            <AssistantMessageActions
                                content={msg.content}
                                question={messages.slice(0, i).findLast((m) => m.role === 'user')?.content ?? ''}
                                onReplay={onReplay}
                                msgIndex={i}
                                ratings={ratings}
                                onRate={onRate}
                                requiresConfirmation={msg.requiresConfirmation}
                                sql={msg.sql}
                                exportToken={msg.exportToken}
                                onConfirmSql={onConfirmSql}
                                t={t}
                            />
                        )}
                    </div>
                    {msg.role === 'user' ? (
                        <div className='mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-semibold uppercase text-white'>
                            {userInitials}
                        </div>
                    ) : null}
                </div>
            ))}
            {isLoading && (
                <div className='flex self-start rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-500 shadow-[0_4px_14px_rgba(15,23,42,0.05)]'>
                    <LoadingDots />
                    <span className='ml-2'>{getLoadingStatusLabel(t, loadingStatus)}</span>
                </div>
            )}
            <div
                ref={messagesEndRef}
                className='h-2 shrink-0'
            />
        </div>
    );
};
